import { createDomain, sample, combine, attach } from 'effector'
import { createGate } from 'effector-react'
import { FormValues, employeeModel } from '~/entities/common/Taxipark/Employee'
import { AxiosErrorType, Employee } from '~/shared/api'
import { logger } from '~/shared/lib/logger'
import { mapMessageErrors } from '~/shared/lib/mapMessageErrors'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { isString } from '~/shared/lib/utils'
import { formButtonsModel } from '~/shared/ui/Form'

export const Gate = createGate<{ employeeId: UniqueId }>()
export const domain = createDomain('features.taxipark.employees.edit')

export const $employeeId = domain
  .createStore<UniqueId | null>(null)
  .on(Gate.state, (_, { employeeId }) => employeeId)

export const $employee = combine(
  $employeeId,
  employeeModel.$employeesCache,
  (id, cache) => {
    if (!id) return null
    return cache.map[id] ?? null
  },
)

sample({
  clock: $employeeId,
  filter: isString,
  target: employeeModel.requestFx,
})

export const employeeUpdateFx = attach({
  effect: employeeModel.saveFx,
  source: $employeeId,
  mapParams: (values: FormValues, id) => {
    const { regionsIds, rolesIds, carRentalsIds, timezone, locale, ...attrs } =
      values
    const employee = new Employee(
      {
        ...attrs,
        ...(isString(timezone) && { timezone }),
        ...(isString(locale) && { locale }),
      },
      id as string,
    )

    if (Array.isArray(regionsIds)) employee.setRegions(regionsIds)
    if (Array.isArray(rolesIds)) employee.setRoles(rolesIds)
    if (Array.isArray(carRentalsIds)) employee.setCarRentals(carRentalsIds)

    return employee
  },
})

export const formSubmitted = domain.createEvent<FormValues>()
sample({
  clock: formSubmitted,
  target: employeeUpdateFx,
})

sample({
  clock: employeeUpdateFx.doneData,
  fn() {
    return {
      messageI18n: 'alert.saved successfully' as const,
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued, formButtonsModel.editingEnded],
})

sample({
  clock: employeeUpdateFx.failData,
  fn(e) {
    logger.error(e)
    return {
      messageI18n: 'alert.failed to save employee data' as const,
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

export const resetPassword = domain.createEvent()
export const resetPasswordFx = domain.createEffect<
  UniqueId,
  void,
  AxiosErrorType
>({
  handler: async (employeeId) => {
    await Employee.resetPassword(employeeId)
  },
})

sample({
  clock: resetPassword,
  source: $employeeId,
  filter: isString,
  target: resetPasswordFx,
})

sample({
  clock: resetPasswordFx.doneData,
  fn() {
    return {
      messageI18n: 'alert.new password sent to email' as const,
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued],
})

sample({
  clock: resetPasswordFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: mapMessageErrors(e),
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})
